import IconArrowBack2 from "@/icons/IconArrowBack2";
import * as React from "react";
import { Link } from "react-router-dom";
import { StickyHeaderContext } from "@/context/StickyHeaderContext";
import { Divider } from "@/components/ds";

interface Props {
  title: string;
  subTitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /** Optional explicit back destination, used only when it needs to point
   *  somewhere the hierarchical breadcrumb's parent crumb would NOT (e.g. a
   *  dynamic /:id sub-route with no menu entry of its own - /ai-new/settings
   *  subpages all set this to jump back to the settings list). MainLayout's
   *  breadcrumb bar already renders above this header for every other case,
   *  including a back-arrow affix on the immediate parent crumb - see
   *  ds/Breadcrumb.tsx. There is no automatic fallback arrow here anymore
   *  (removed 2026-07-17, QA report: it duplicated the breadcrumb's own back
   *  cue and its goBack()/useNavigationTrail wiring was broken). */
  backLink?: string;
  tabs?: React.ReactNode;
}

export default function StickyRouteHeader(props: Props) {
  const { setHasStickyHeader, toggleSidebar } = React.useContext(StickyHeaderContext);

  // Signal MainLayout synchronously (before paint). MainLayout still renders its
  // breadcrumb bar above this header regardless (see MainLayout.tsx trail.length > 1) -
  // this flag only tells MainLayout to drop the <main> top padding it would otherwise
  // add, since this header supplies its own.
  React.useLayoutEffect(() => {
    setHasStickyHeader(true);
    return () => setHasStickyHeader(false);
  }, [setHasStickyHeader]);

  const px = toggleSidebar ? "px-6" : "px-16";
  const contentPad = toggleSidebar ? "p-3 sm:p-6 pt-4" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16 pt-4";

  return (
    <>
      <header
        className="shrink-0 sticky top-0 z-50 bg-white"
        style={{ isolation: "isolate" }}
      >
        <div className={`flex items-center justify-between gap-3 py-3 ${px}`}>
          <div className="flex min-w-0 flex-row items-center gap-2">
            {props.backLink && (
              <span className="shrink-0">
                <Link to={props.backLink} aria-label="Back">
                  <IconArrowBack2 />
                </Link>
              </span>
            )}
            <div className="flex min-w-0 flex-col">
              <h1 className="m-0 truncate text-base font-semibold leading-snug text-zinc-800">{props.title}</h1>
              {props.subTitle && (
                <span className="mt-0.5 text-sm font-normal text-zinc-500 dark:text-zinc-300">
                  {props.subTitle}
                </span>
              )}
            </div>
          </div>
          {props.headerActions && <span className="shrink-0">{props.headerActions}</span>}
        </div>

        {props.tabs && (
          <nav aria-label="page tabs" className={`flex overflow-x-auto ${px}`}>
            {props.tabs}
          </nav>
        )}

        <Divider className="my-0" />
      </header>

      {/* Content wrapper restores the padding that <main> no longer provides */}
      <div className={contentPad}>
        {props.children}
      </div>
    </>
  );
}
